import {
  Stack,
  Button,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  Paper,
  StackProps,
  Typography,
  styled,
  Input,
  InputProps,
  CircularProgress,
  IconButton,
  Divider,
  List,
  ListItemButton,
  useTheme,
  Avatar,
  Box,
} from '@mui/material'
import React, { FC, PropsWithChildren, useMemo, useState } from 'react'
import { HiChevronDown, HiChevronRight, HiOutlineSwitchVertical } from 'react-icons/hi'
import Touchable from '@components/Touchable'
import { sanitizeDecimalInput } from '@plug-core/utils/validationHelper'
import { ExchangeToken, InputError } from '@plug-core/interfaces/swapInterface'
import { ChainOption } from '@plug-core/interfaces/common'
import { formatMoney, formatNumber } from '@plug-core/utils/formatNumber'
import useConfig from '@plug-core/hooks/useConfig'
import { IoIosArrowForward } from 'react-icons/io'
import { SwapPairPrice } from '@plug-core/hooks/useSwap'
import useBalances from '@plug-core/hooks/useBalances'
import useToken from '@plug-core/hooks/useToken'
import { StealthEx } from '@assets/icons/icons'
import TokenAvatar from '@components/TokenAvatar'
import TokenPicker from './Components/TokenPicker'
import { SwapProviderSelect } from './Components/SwapProviderSelect'
import { useTranslation } from 'react-i18next'
import { getChainIcon } from '@plug-core/utils/getChainIcon'
import ChainPicker from '@components/ChainPicker'
import { ODINBTC_ID } from '@plug-core/constants/tokenIds'

interface TokenCardProps<TokenType extends ExchangeToken = ExchangeToken> {
  token?: TokenType
  onTokenSelect?: (token: TokenType | undefined) => void
  showBalance?: boolean
  amount?: string
  setAmount: (newAmount: string) => void
  maxAmount?: number
  tokensForSelect?: TokenType[]
  mode: 'swap' | 'convert' | 'stake' | 'mint'
  disablePriceInput?: boolean
  error?: string | undefined
  selectTokenTitle?: string
  disableTokenPicker?: boolean
}

const PriceInputField = styled(Input)<InputProps>(({ theme }) => ({
  '& input': {
    fontSize: 20,
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
}))

export const TokenCard = <T extends ExchangeToken = ExchangeToken>({
  token,
  onTokenSelect,
  showBalance,
  amount,
  setAmount,
  maxAmount,
  tokensForSelect,
  mode,
  disablePriceInput,
  error,
  selectTokenTitle,
  disableTokenPicker,
}: TokenCardProps<T>): JSX.Element => {
  const { t } = useTranslation()
  const { tokenPricesWithChange } = useConfig()
  const { balances, findBalance } = useBalances()
  const { id, decimals } = useToken(token?.id)

  let tokenPrice
  if (id) {
    tokenPrice = id === ODINBTC_ID ? tokenPricesWithChange.btc : tokenPricesWithChange[id]
  }

  let balance
  if (mode === 'convert') {
    balance = balances.find((b) => b.token.symbol === token?.symbol)?.balance
  } else {
    balance = findBalance({ id })?.balance
  }

  const [tokenPickerVisible, setTokenPickerVisible] = useState(false)

  const HoverTokenStack = styled(Stack)<StackProps>(({ theme }) => ({
    '&:hover': {
      borderRadius: 10,
      backgroundColor: theme.palette.background.default,
    },
  }))

  return (
    <Stack direction="column" component={Paper} py={1.1} px={1.25}>
      <HoverTokenStack px={0.5}>
        <Touchable disableHover style={{ flex: 1 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            onClick={() => setTokenPickerVisible(true)}
            p={1}
          >
            <ListItemAvatar>
              <Stack position="relative">
                <TokenAvatar logo={token?.logo} />
                <Stack
                  position="absolute"
                  right={12}
                  bottom={-6}
                  bgcolor="background.paper"
                  padding={0.45}
                  borderRadius={4}
                >
                  {token?.chain && (
                    <img src={getChainIcon(token?.chain || '')} height={14} width={14} />
                  )}
                </Stack>
              </Stack>
            </ListItemAvatar>
            <ListItemText primary={token?.name || '...'} />
            {!disableTokenPicker && (
              <ListItemIcon sx={{ justifyContent: 'flex-end' }}>
                <HiChevronRight color="white" size={20} />
              </ListItemIcon>
            )}
          </Stack>
        </Touchable>
      </HoverTokenStack>

      <Stack px={2}>
        <Stack flexDirection="row" alignItems="center" justifyContent="space-between">
          <PriceInputField
            disabled={disablePriceInput}
            disableUnderline
            value={amount}
            placeholder="0.0"
            onChange={(e) => sanitizeDecimalInput(e.target.value, setAmount)}
            sx={{
              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: 'white',
              },
            }}
          />

          <Stack flexDirection="row">
            {typeof maxAmount === 'number' &&
              [
                { label: '50%', amount: maxAmount / 2 },
                { label: 'MAX', amount: maxAmount },
              ].map(({ amount, label }) => {
                return (
                  <Touchable key={label} scaleFactor={0.1}>
                    <Box
                      onClick={() => setAmount(amount > 0 ? amount.toFixed(decimals) : '0')}
                      bgcolor={(theme) => theme.palette.background.default}
                      px={0.8}
                      py={0.4}
                      borderRadius={1}
                      ml={0.5}
                    >
                      <Typography fontSize={12}>{label}</Typography>
                    </Box>
                  </Touchable>
                )
              })}
          </Stack>
        </Stack>

        {error && (
          <Typography fontWeight="bold" fontSize={13} color="red">
            {error}
          </Typography>
        )}

        <Stack flexDirection="row" alignItems="center">
          {tokenPrice && (
            <Typography textAlign="right" variant="body2" color="text.secondary" fontSize={13}>
              {formatMoney(tokenPrice.price * Number(amount || '0'))}
            </Typography>
          )}

          {showBalance && (
            <Typography
              textAlign="right"
              variant="body2"
              color="text.secondary"
              ml="auto"
              fontSize={13}
            >
              {t('swap.balance')}: {formatNumber(balance || 0)}
            </Typography>
          )}
        </Stack>

        {!disableTokenPicker && (
          <TokenPicker
            visible={tokenPickerVisible}
            tokensForSelect={tokensForSelect!}
            mode={mode}
            selectTokenTitle={selectTokenTitle}
            onTokenSelect={(t) => onTokenSelect?.(t)}
            onClose={() => setTokenPickerVisible(false)}
          />
        )}
      </Stack>
    </Stack>
  )
}

interface Props extends PropsWithChildren {
  fromToken?: ExchangeToken
  toToken?: ExchangeToken
  setToToken: (id: string | undefined) => void
  setFromToken: (id: string | undefined) => void
  amount?: string
  setAmount: (amount: string) => void
  toAmount?: string
  setToAmount: (amount: string) => void
  maxAmount?: number
  priceHuman?: string
  error?: InputError
  currentQuote?: SwapPairPrice
  tokens: ExchangeToken[]
  destinationTokens: ExchangeToken[]
  mode: 'swap' | 'convert'
  nextTitle: string
  handleSubmit: () => void
  chainMode: ChainOption
  setChainMode: (chain: ChainOption) => void
  chains: ChainOption[]
}

const ExchangeView: FC<Props> = ({
  toToken,
  setFromToken,
  fromToken,
  setToToken,
  amount,
  setAmount,
  toAmount,
  setToAmount,
  maxAmount,
  priceHuman,
  error,
  currentQuote,
  tokens,
  destinationTokens,
  nextTitle,
  handleSubmit,
  children,
  mode,
  chainMode,
  setChainMode,
  chains,
}) => {
  const [isSwitched, setIsSwitched] = useState(false)
  const [chainSelectVisible, setChainSelectVisible] = useState(false)
  const [swapProviderSelectVisible, setSwapProviderSelectVisible] = useState(false)

  const { palette } = useTheme()
  const { t } = useTranslation()

  const switchTokens = () => {
    setIsSwitched((prevstate) => !prevstate)

    setFromToken(toToken?.id)
    setToToken(fromToken?.id)
  }

  const filteredSourceTokens = useMemo(() => {
    if (chainMode === 'all') {
      return tokens
    } else {
      return tokens.filter((b: ExchangeToken) => b.chain === chainMode)
    }
  }, [tokens, chainMode])

  const filteredDestinationTokens = useMemo(() => {
    if (chainMode === 'all') {
      return destinationTokens
    } else {
      return destinationTokens.filter((b: ExchangeToken) => b.chain === chainMode)
    }
  }, [destinationTokens, chainMode])

  const renderSwitchButton = () => {
    return (
      <Stack flexGrow={1} alignItems={'center'} mt={`-16px !important`} mb={`-24px !important`}>
        <IconButton
          onClick={switchTokens}
          sx={{
            color: (theme) => theme.palette.text.primary,
            boxSizing: 'border-box',
            bgcolor: (theme) => theme.palette.background.dialog,
            '&:hover': {
              bgcolor: (theme) => theme.palette.background.default,
            },
            '&:disabled': {
              bgcolor: (theme) => theme.palette.background.default,
            },
          }}
        >
          {false ? (
            <Stack>
              <CircularProgress size={16} />
            </Stack>
          ) : (
            <HiOutlineSwitchVertical
              style={{
                animation: isSwitched
                  ? 'rotateFrom 100ms ease-in-out alternate both'
                  : 'rotateTo 100ms ease-in-out alternate both',
              }}
            />
          )}
        </IconButton>
      </Stack>
    )
  }

  const renderOptions = () => {
    return (
      <List component={Paper} disablePadding>
        <ListItemButton
          disabled={!currentQuote}
          onClick={() => setSwapProviderSelectVisible(true)}
          sx={{ display: 'flex', justifyContent: 'space-between', my: 0.4 }}
        >
          <Typography variant="body2">Provider</Typography>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
            <Typography variant="body2" color="text.secondary">
              {currentQuote?.pair.provider}
            </Typography>
            <IoIosArrowForward style={{ color: '#A3A3A3' }} />
          </Stack>
        </ListItemButton>

        <Divider sx={{ borderColor: (theme) => theme.palette.background.default }} />

        {typeof currentQuote?.slippage === 'number' && (
          <>
            <ListItemButton
              disabled
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                my: 0.4,
                '&.Mui-disabled': { opacity: 1 },
              }}
            >
              <Typography variant="body2">Slippage</Typography>
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
                <Typography variant="body2" color="text.secondary">
                  {formatNumber(currentQuote?.slippage, '%')}
                </Typography>
              </Stack>
            </ListItemButton>

            <Divider sx={{ borderColor: (theme) => theme.palette.background.default }} />
          </>
        )}

        <ListItemButton
          disabled
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            my: 0.4,
            '&.Mui-disabled': { opacity: 1 },
          }}
        >
          <Typography variant="body2">Price</Typography>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
            <Typography variant="body2" color="text.secondary">
              {priceHuman}
            </Typography>
          </Stack>
        </ListItemButton>
      </List>
    )
  }

  return (
    <Stack spacing={1.25} mx={2} mb={1.25} display="flex" direction="column">
      {chains.length > 0 && (
        <Stack component={Paper}>
          <ListItemButton
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              '&.Mui-disabled': { opacity: 1 },
              my: 1,
              px: 0.75,
              py: 0.1,
            }}
            onClick={() => setChainSelectVisible(true)}
          >
            <Stack direction="row" alignItems="center" spacing={1} ml={1}>
              <Avatar sx={{ width: 30, height: 30 }} src={getChainIcon(chainMode)} />
              <Typography>{t(`chains.long.${chainMode}`)}</Typography>
            </Stack>
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
              <Stack justifyContent="center" alignItems="center" direction="row">
                <HiChevronDown color={palette.text.secondary} size={25} />
              </Stack>
            </Stack>
          </ListItemButton>
        </Stack>
      )}
      <Stack direction="column" spacing={1.25}>
        <Stack spacing={0.5}>
          <TokenCard
            token={fromToken}
            onTokenSelect={(token) => setFromToken(token?.id)}
            showBalance
            amount={amount}
            maxAmount={maxAmount}
            setAmount={setAmount}
            tokensForSelect={filteredSourceTokens}
            mode={mode}
            error={error?.inputFieldError}
            selectTokenTitle="Select Source Token"
          />
          {renderSwitchButton()}
          <TokenCard
            token={toToken}
            onTokenSelect={(token) => setToToken(token?.id)}
            amount={toAmount}
            setAmount={setToAmount}
            tokensForSelect={filteredDestinationTokens}
            mode={mode}
            selectTokenTitle="Select Destination Token"
            disablePriceInput
          />
        </Stack>
        {mode === 'swap' && renderOptions()}
        <Button variant="rainbow" disabled={!!error} onClick={handleSubmit} fullWidth>
          {error?.buttonError || nextTitle}
        </Button>
        {mode === 'convert' && (
          <>
            <Typography justifyContent="center" display="flex" variant="body2">
              Powered by
            </Typography>
            <StealthEx height={16} width="auto" />
          </>
        )}
        {mode === 'swap' && (
          <SwapProviderSelect
            visible={swapProviderSelectVisible}
            onClose={() => setSwapProviderSelectVisible(false)}
          />
        )}
        <ChainPicker
          chains={chains}
          initialChain={chainMode}
          chainSelectVisible={chainSelectVisible}
          setChainSelectVisible={setChainSelectVisible}
          onChainChange={setChainMode}
        />
      </Stack>
      {children}
    </Stack>
  )
}

export default ExchangeView
